import api from "../utils/api";
import { isGuid } from "../utils/string";
import { isEmail } from "../utils/regex";
import {
  LOGOUT
} from "./types";

const failure = (err, fallback) => ({
    success: false,
    fatal: true,
    status: err?.response?.status || 500,
    message: err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || fallback,
    error: err?.response?.data?.error || err?.response?.data || null
});

const normalizeNameText = (value) => {
    if (value === undefined) return undefined;
    if (value === null) return null;

    const text = String(value).replace(/\s+/g, " ").trim();
    return text || null;
};

const normalizePersonNamePayload = ({ displayName, firstName, middleName, lastName, generation, ...payload }) => {
    return {
        ...payload,
        firstName: normalizeNameText(firstName),
        middleName: normalizeNameText(middleName),
        lastName: normalizeNameText(lastName),
        generation: normalizeNameText(generation),
        displayName: normalizeNameText(displayName)
    };
};

export const me = () => async (dispatch) => {
    try {
        const res = await api.get(`/people/me`);
        return res.data;
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
};

export const mine = ({ displayName, firstName, middleName, lastName, generation, email, birthDate, phone, fax, residenceUuid, Residence, communicationPreferences, ssn }) => async (dispatch) => {
    try {
        const res = await api.post(`/people/me`, normalizePersonNamePayload({ displayName, firstName, middleName, lastName, generation, email, birthDate, phone, fax, residenceUuid, Residence, communicationPreferences, ssn }));
        return res.data;
    } catch (error) {
        dispatch({
            type: LOGOUT
        });
    }
}

export const save = ({ uuid, displayName, firstName, middleName, lastName, generation, email, birthDate, deceasedDate, phone, fax, residenceUuid, Residence, communicationPreferences, userUuid, birthplaceUuid, Birthplace, deceaseUuid, Decease, gender, religion, race, ssn }) => async (dispatch) => {
    try {
        const res = await api.post(`/people`, normalizePersonNamePayload({ uuid, displayName, firstName, middleName, lastName, generation, email, birthDate, deceasedDate, phone, fax, residenceUuid, Residence, communicationPreferences, userUuid, birthplaceUuid, Birthplace, deceaseUuid, Decease, gender, religion, race, ssn }));
        return res.data;
    } catch (error) {
        if (error?.response?.status === 401) dispatch({ type: LOGOUT });
        return failure(error, "Person save failed");
    }
}

export const search = ({ page = 0, size = 10, order = "lastName", direction = "asc", search = null, flags = 0, archived = 0 }) => async (dispatch) => {
    try {
        const res = await api.get(`/people/p/${page}/s/${size}/o/${order}/${direction}/f/${flags}/${search}`, {
            params: { archived }
        });
        return res.data;
    } catch (err) {
        dispatch({
            type: LOGOUT,
        });
    }
}

export const get = ({ uuid, email }) => async (dispatch) => {
    try {
        const res = isGuid(uuid ?? null) ? await api.get(`/people/i/${uuid}`) : isEmail(email) ? await api.get(`/people/e/${email}`) : { data: { success: false }} ;
        return res.data;
    } catch (err) {
        if (err?.response?.status === 401) dispatch({ type: LOGOUT });
        return null;
    }
}

export const destroy = ({ uuid, selection = null, profileAction = "delete" }) => async (dispatch) => {
    try {
        const res = await api.delete(`/people/i/${uuid}`, {
            data: {
                selection,
                profileAction
            }
        });
        return res.data;
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
}

export const archive = ({ uuid, archive = true }) => async (dispatch) => {
    try {
        const res = await api.post(`/people/i/${uuid}/archive`, { archive });
        return res.data;
    } catch (err) {
        dispatch({
            type: LOGOUT
        });
    }
}

export const completeExtract = ({ uuid, name = "person-complete-extract.zip", selection = null }) => async () => {
    const response = selection
        ? await api.post(
            `/people/i/${uuid}/extract`,
            { selection },
            {
                responseType: "blob"
            }
        )
        : await api.get(
            `/people/i/${uuid}/extract`,
            {
                responseType: "blob"
            }
        );

    const blob = new Blob([response.data], {
        type: response.headers["content-type"] || "application/zip"
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.setAttribute("download", name);
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
}

export const importPreview = ({ file }) => async () => {
    const form = new FormData();
    form.append("file", file);

    const res = await api.post(`/people/import/preview`, form, {
        headers: {
            "Content-Type": "multipart/form-data"
        }
    });

    return res.data;
}

export const importApply = ({ file, resolutions = {} }) => async () => {
    const form = new FormData();
    form.append("file", file);
    form.append("resolutions", JSON.stringify(resolutions));

    const res = await api.post(`/people/import/apply`, form, {
        headers: {
            "Content-Type": "multipart/form-data"
        }
    });

    return res.data;
}
